// Página AdminHomeSettings - Configuración de la página de inicio (Hero)
// Permite gestionar la imagen de fondo, colores de texto y contenido del Hero

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  getSiteSettings, 
  updateHeroSettings
} from '../../services/siteSettings.service'
import { uploadImage } from '../../services/cloudinary.service'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import './AdminHomeSettings.css'

/**
 * Página AdminHomeSettings
 * Gestión completa de la configuración del Hero
 */
function AdminHomeSettings() {
  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState(null)
  
  const [galleryFiles, setGalleryFiles] = useState([])
  const [galleryPreviews, setGalleryPreviews] = useState([])
  const [previewDurations, setPreviewDurations] = useState([])
  const [uploadingImage, setUploadingImage] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [videoInput, setVideoInput] = useState('')

  // Cargar configuración
  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getSiteSettings()
      setSettings(data)
      setGalleryFiles([])
      setGalleryPreviews([])
      setPreviewDurations([])
    } catch (err) {
      console.error('Error cargando configuración:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const isValidImageFile = (file) => {
    if (!file?.type?.startsWith('image/')) {
      setNotification({
        type: 'error',
        message: 'Por favor selecciona un archivo de imagen válido'
      })
      setTimeout(() => setNotification(null), 3000)
      return false
    }
    if (file.size > 5 * 1024 * 1024) {
      setNotification({
        type: 'error',
        message: 'La imagen no debe superar los 5MB'
      })
      setTimeout(() => setNotification(null), 3000)
      return false
    }
    return true
  }

  const normalizeDuration = (value, fallback) => {
    const num = Number(value)
    if (Number.isFinite(num) && num > 0) return num
    return fallback
  }

  const getDefaultDuration = (currentSettings) => {
    const fallback = normalizeDuration(currentSettings?.hero?.slideDurationSeconds, 5.5)
    return fallback
  }

  const updateMediaDuration = (url, value) => {
    setSettings(prev => {
      const fallback = getDefaultDuration(prev)
      const nextMap = { ...(prev?.hero?.mediaDurations || {}) }
      if (value === '') {
        delete nextMap[url]
      } else {
        nextMap[url] = normalizeDuration(value, fallback)
      }
      return {
        ...prev,
        hero: {
          ...(prev?.hero || {}),
          mediaDurations: nextMap
        }
      }
    })
  }

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return
    const defaultDuration = getDefaultDuration(settings)
    const validFiles = []
    const validPreviews = []
    const nextDurations = []
    files.forEach((file) => {
      if (!isValidImageFile(file)) return
      validFiles.push(file)
      nextDurations.push(defaultDuration)
      try {
        validPreviews.push(URL.createObjectURL(file))
      } catch {
        validPreviews.push(null)
      }
    })
    if (validFiles.length === 0) return
    setGalleryFiles(prev => [...prev, ...validFiles])
    setGalleryPreviews(prev => [...prev, ...validPreviews.filter(Boolean)])
    setPreviewDurations(prev => [...prev, ...nextDurations])
    e.target.value = ''
  }

  const removeExistingImage = (url) => {
    setSettings(prev => ({
      ...prev,
      hero: {
        ...(prev.hero || {}),
        backgroundImages: (prev.hero?.backgroundImages || []).filter(img => img !== url),
        mediaDurations: Object.fromEntries(
          Object.entries(prev.hero?.mediaDurations || {}).filter(([key]) => key !== url)
        )
      }
    }))
  }

  const removePreviewImage = (index) => {
    setGalleryFiles(prev => prev.filter((_, i) => i !== index))
    setPreviewDurations(prev => prev.filter((_, i) => i !== index))
    setGalleryPreviews(prev => {
      const next = prev.filter((_, i) => i !== index)
      const removed = prev[index]
      if (removed) {
        try {
          URL.revokeObjectURL(removed)
        } catch {
          return next
        }
      }
      return next
    })
  }

  const handleAddVideo = () => {
    const value = String(videoInput || '').trim()
    if (!value) return
    setSettings(prev => ({
      ...prev,
      hero: {
        ...(prev.hero || {}),
        embeddedVideos: Array.from(new Set([...(prev.hero?.embeddedVideos || []), value]))
      }
    }))
    setVideoInput('')
  }

  const handleRemoveVideo = (url) => {
    setSettings(prev => ({
      ...prev,
      hero: {
        ...(prev.hero || {}),
        embeddedVideos: (prev.hero?.embeddedVideos || []).filter(item => item !== url),
        mediaDurations: Object.fromEntries(
          Object.entries(prev.hero?.mediaDurations || {}).filter(([key]) => key !== url)
        )
      }
    }))
  }

  // Manejar cambios en el formulario
  const handleHeroChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      hero: {
        ...(prev.hero || {}),
        [field]: value
      }
    }))
  }

  // Guardar configuración
  const handleSaveSettings = async () => {
    try {
      setSaving(true)
      
      const existingImages = Array.isArray(settings.hero?.backgroundImages)
        ? settings.hero.backgroundImages.filter(Boolean)
        : []
      const defaultDuration = getDefaultDuration(settings)
      const uploadedImages = []
      const uploadedDurations = {}
      
      if (galleryFiles.length > 0) {
        setUploadingImage(true)
        setUploadProgress(0)
        
        try {
          let completed = 0
          for (let index = 0; index < galleryFiles.length; index += 1) {
            const file = galleryFiles[index]
            const uploadResult = await uploadImage(file, 'hero', (progress) => {
              const base = completed / galleryFiles.length
              const current = progress / 100 / galleryFiles.length
              setUploadProgress(Math.round((base + current) * 100))
            })
            if (uploadResult?.url) {
              uploadedImages.push(uploadResult.url)
              const durationValue = previewDurations[index]
              uploadedDurations[uploadResult.url] = normalizeDuration(durationValue, defaultDuration)
            }
            completed += 1
            setUploadProgress(Math.round((completed / galleryFiles.length) * 100))
          }
        } catch (err) {
          console.error('Error subiendo imagen:', err)
          setNotification({
            type: 'error',
            message: `Error subiendo imagen: ${err.message}`
          })
          setTimeout(() => setNotification(null), 5000)
          setUploadingImage(false)
          return
        } finally {
          setUploadingImage(false)
          setUploadProgress(0)
        }
      }
      
      const allImages = [...existingImages, ...uploadedImages].filter(Boolean)
      const backgroundImage = allImages[0] || null
      const embeddedVideos = Array.isArray(settings.hero?.embeddedVideos)
        ? settings.hero.embeddedVideos.filter(Boolean)
        : []
      const existingDurations = settings.hero?.mediaDurations && typeof settings.hero.mediaDurations === 'object'
        ? settings.hero.mediaDurations
        : {}
      const mediaDurations = {}
      existingImages.forEach((url) => {
        if (existingDurations[url] !== undefined) {
          mediaDurations[url] = normalizeDuration(existingDurations[url], defaultDuration)
        }
      })
      embeddedVideos.forEach((url) => {
        if (existingDurations[url] !== undefined) {
          mediaDurations[url] = normalizeDuration(existingDurations[url], defaultDuration)
        }
      })
      Object.assign(mediaDurations, uploadedDurations)
      const durationRaw = Number(settings.hero?.slideDurationSeconds)
      const slideDurationSeconds = Number.isFinite(durationRaw) && durationRaw > 0 ? durationRaw : 5.5
      await updateHeroSettings({
        ...settings.hero,
        backgroundImage,
        backgroundImages: allImages,
        embeddedVideos,
        slideDurationSeconds,
        mediaDurations
      })
      
      setNotification({
        type: 'success',
        message: 'Configuración del Hero guardada correctamente'
      })
      setTimeout(() => setNotification(null), 3000)
      
      // Recargar configuración para actualizar el estado
      await loadSettings()
      setGalleryFiles([])
      setGalleryPreviews([])
    } catch (err) {
      console.error('Error guardando configuración:', err)
      setNotification({
        type: 'error',
        message: `Error: ${err.message}`
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="admin-home-settings-page">
        <div className="loading-state">Cargando configuración...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="admin-home-settings-page">
        <div className="error-state">Error: {error}</div>
      </div>
    )
  }

  const heroImages = Array.isArray(settings?.hero?.backgroundImages)
    ? settings.hero.backgroundImages.filter(Boolean)
    : (settings?.hero?.backgroundImage ? [settings.hero.backgroundImage] : [])
  const mediaDurations = settings?.hero?.mediaDurations && typeof settings.hero.mediaDurations === 'object'
    ? settings.hero.mediaDurations
    : {}
  const defaultDuration = getDefaultDuration(settings)
  const previewImage = galleryPreviews[0] || heroImages[0] || null

  return (
    <div className="admin-home-settings-page">
      <div className="home-settings-header">
        <div>
          <h1>Configuración de Página de Inicio</h1>
          <p>Gestiona el contenido y apariencia del Hero (sección principal)</p>
        </div>
        <div className="header-actions">
          <Link to="/admin/configuraciones">
            <Button variant="secondary">Volver</Button>
          </Link>
        </div>
      </div>

      {/* Vista previa del Hero */}
      <div className="hero-preview-section">
        <h2>Vista Previa</h2>
        <div 
          className="hero-preview"
          style={{
            backgroundImage: previewImage 
              ? `url(${previewImage})` 
              : 'linear-gradient(135deg, #2c3e50 0%, #d4a574 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            color: settings.hero?.textColor || 'white'
          }}
        >
          <div className="hero-preview-content">
            <h1>{settings.hero?.title || 'Transforma Tu Hogar'}</h1>
            <p>{settings.hero?.subtitle || 'Descubre muebles de diseño exclusivo con hasta 40% OFF'}</p>
            <button className="hero-preview-button">
              {settings.hero?.buttonText || 'Explorar Colección'}
            </button>
          </div>
        </div>
      </div>

      {/* Formulario de configuración */}
      <div className="settings-form">
        {/* Sección: Imagen de Fondo */}
        <div className="settings-section">
          <h3>Galería del Hero</h3>
          <p className="section-description">
            Subí varias imágenes para construir la galería de inicio.
          </p>
          <div className="form-group">
            <label htmlFor="hero-slide-duration">Duración por slide (segundos)</label>
            <input
              type="number"
              id="hero-slide-duration"
              value={settings.hero?.slideDurationSeconds ?? 5.5}
              onChange={(e) => {
                const nextValue = e.target.value
                handleHeroChange('slideDurationSeconds', nextValue === '' ? '' : Number(nextValue))
              }}
              min="1"
              step="0.5"
              disabled={saving}
              className="form-input"
            />
            <small className="form-hint">Aplica a fotos y videos del slider del inicio.</small>
          </div>

          {(heroImages.length > 0 || galleryPreviews.length > 0) && (
            <div className="image-gallery-grid">
              {heroImages.map((url, index) => (
                <div key={`existing-${url}-${index}`} className="image-preview-container">
                  <img src={url} alt="Preview" className="image-preview" />
                  <button
                    type="button"
                    onClick={() => removeExistingImage(url)}
                    className="remove-image-btn"
                    disabled={saving || uploadingImage}
                  >
                    ✕ Eliminar
                  </button>
                  <div className="media-duration-row">
                    <label htmlFor={`hero-duration-existing-${index}`}>Duración (s)</label>
                    <input
                      id={`hero-duration-existing-${index}`}
                      type="number"
                      min="1"
                      step="0.5"
                      value={mediaDurations[url] ?? defaultDuration}
                      onChange={(e) => updateMediaDuration(url, e.target.value)}
                      disabled={saving || uploadingImage}
                      className="form-input media-duration-input"
                    />
                  </div>
                </div>
              ))}
              {galleryPreviews.map((url, index) => (
                <div key={`new-${url}-${index}`} className="image-preview-container">
                  <img src={url} alt="Preview" className="image-preview" />
                  <button
                    type="button"
                    onClick={() => removePreviewImage(index)}
                    className="remove-image-btn"
                    disabled={saving || uploadingImage}
                  >
                    ✕ Quitar
                  </button>
                  <div className="media-duration-row">
                    <label htmlFor={`hero-duration-preview-${index}`}>Duración (s)</label>
                    <input
                      id={`hero-duration-preview-${index}`}
                      type="number"
                      min="1"
                      step="0.5"
                      value={previewDurations[index] ?? defaultDuration}
                      onChange={(e) => {
                        const nextValue = e.target.value
                        setPreviewDurations(prev => prev.map((item, i) => {
                          if (i !== index) return item
                          if (nextValue === '') return ''
                          return nextValue
                        }))
                      }}
                      disabled={saving || uploadingImage}
                      className="form-input media-duration-input"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="image-upload-area">
            <input
              type="file"
              id="hero-image"
              accept="image/*"
              multiple
              onChange={handleImageChange}
              disabled={saving || uploadingImage}
              className="file-input"
            />
            <label htmlFor="hero-image" className="file-label">
              <span className="upload-icon">📷</span>
              <span>Haz clic para subir imágenes</span>
              <small>Formatos: JPG, PNG, WEBP (máx. 5MB)</small>
            </label>
          </div>

          {uploadingImage && (
            <div className="upload-progress">
              <div className="progress-item">
                <span>Subiendo imagen...</span>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <span>{uploadProgress}%</span>
              </div>
            </div>
          )}

        </div>

        <div className="settings-section">
          <h3>Videos embebidos</h3>
          <p className="section-description">
            Pegá links de YouTube, TikTok, Instagram o Facebook para mostrarlos en la portada.
          </p>
          <div className="video-input-row">
            <input
              type="url"
              value={videoInput}
              onChange={(e) => setVideoInput(e.target.value)}
              placeholder="https://"
              className="form-input"
              disabled={saving}
            />
            <Button type="button" variant="secondary" onClick={handleAddVideo} disabled={saving || !videoInput.trim()}>
              Agregar
            </Button>
          </div>
          {(settings.hero?.embeddedVideos || []).length > 0 && (
            <div className="video-list">
              {settings.hero?.embeddedVideos.map((url, index) => (
                <div key={`${url}-${index}`} className="video-item">
                  <div className="video-info">
                    <span className="video-url">{url}</span>
                    <div className="media-duration-row">
                      <label htmlFor={`hero-duration-video-${index}`}>Duración (s)</label>
                      <input
                        id={`hero-duration-video-${index}`}
                        type="number"
                        min="1"
                        step="0.5"
                        value={mediaDurations[url] ?? defaultDuration}
                        onChange={(e) => updateMediaDuration(url, e.target.value)}
                        disabled={saving}
                        className="form-input media-duration-input"
                      />
                    </div>
                  </div>
                  <button type="button" className="remove-image-btn" onClick={() => handleRemoveVideo(url)} disabled={saving}>
                    ✕ Quitar
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sección: Color del Texto */}
        <div className="settings-section">
          <h3>Color del Texto</h3>
          <p className="section-description">
            Selecciona el color del texto que se mostrará sobre la imagen de fondo. 
            Elige blanco para fondos oscuros y negro para fondos claros.
          </p>
          <div className="color-selector">
            <label className="color-option">
              <input
                type="radio"
                name="textColor"
                value="white"
                checked={settings.hero?.textColor === 'white'}
                onChange={(e) => handleHeroChange('textColor', e.target.value)}
                disabled={saving}
              />
              <span className="color-preview white">Texto Blanco</span>
            </label>
            <label className="color-option">
              <input
                type="radio"
                name="textColor"
                value="black"
                checked={settings.hero?.textColor === 'black'}
                onChange={(e) => handleHeroChange('textColor', e.target.value)}
                disabled={saving}
              />
              <span className="color-preview black">Texto Negro</span>
            </label>
          </div>
        </div>

        {/* Sección: Contenido del Hero */}
        <div className="settings-section">
          <h3>Contenido del Hero</h3>

          <div className="form-group">
            <label htmlFor="hero-page-title">Título de la pestaña (navegador)</label>
            <input
              type="text"
              id="hero-page-title"
              value={settings.hero?.pageTitle || ''}
              onChange={(e) => handleHeroChange('pageTitle', e.target.value)}
              placeholder="Ej: PinoHome - Amoblamientos"
              disabled={saving}
              className="form-input"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="hero-title">Título Principal</label>
            <input
              type="text"
              id="hero-title"
              value={settings.hero?.title || ''}
              onChange={(e) => handleHeroChange('title', e.target.value)}
              placeholder="Ej: Transforma Tu Hogar"
              disabled={saving}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="hero-subtitle">Subtítulo</label>
            <textarea
              id="hero-subtitle"
              value={settings.hero?.subtitle || ''}
              onChange={(e) => handleHeroChange('subtitle', e.target.value)}
              placeholder="Ej: Descubre muebles de diseño exclusivo con hasta 40% OFF"
              disabled={saving}
              className="form-textarea"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label htmlFor="hero-button-text">Texto del Botón</label>
            <input
              type="text"
              id="hero-button-text"
              value={settings.hero?.buttonText || ''}
              onChange={(e) => handleHeroChange('buttonText', e.target.value)}
              placeholder="Ej: Explorar Colección"
              disabled={saving}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="hero-button-link">Enlace del Botón</label>
            <input
              type="text"
              id="hero-button-link"
              value={settings.hero?.buttonLink || ''}
              onChange={(e) => handleHeroChange('buttonLink', e.target.value)}
              placeholder="Ej: /productos"
              disabled={saving}
              className="form-input"
            />
            <small className="form-hint">Ruta relativa, ej: /productos, /categorias, etc.</small>
          </div>
        </div>

        {/* Botón de guardar */}
        <div className="settings-footer">
          <Button
            variant="primary"
            size="large"
            onClick={handleSaveSettings}
            disabled={saving || uploadingImage}
          >
            {saving || uploadingImage ? 'Guardando...' : 'Guardar Configuración'}
          </Button>
        </div>
      </div>

      {notification && (
        <Toast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  )
}

export default AdminHomeSettings

